// AdminBillingPage
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  CircularProgress,
  IconButton,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TablePagination from "@mui/material/TablePagination";
import * as XLSX from "xlsx";
import { getAllDNROrders } from "../services/api";
import LsService, { storageKey } from "../services/localstorage";
import {
  headerBoxStyle,
  lrgScreenStyle,
  rightInnerBoxStyle,
} from "../components/panelComponents/panelStyles";
import DNRMainNavHeader from "../components/panelComponents/DNRMainNavHeader";
import SmallScreenError from "../components/panelComponents/SmallScreenError";
import { layoutDarkGreenColor, layoutLightGreenColor } from "../data/contents";

function Row({ order }) {
  const [open, setOpen] = useState(false);
  //   console.log(order);

  return (
    <>
      {/* Parent Row = Order */}
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{order.sno}</TableCell>
        <TableCell>INV{order.invoice_number}</TableCell>

        <TableCell>
          {order.order_date} {order.order_time}
        </TableCell>
        <TableCell>{order.paymentMethod}</TableCell>
        <TableCell>₹{order.gst}</TableCell>
        <TableCell>₹{order.total}</TableCell>
      </TableRow>

      {/* Collapsible Child Row = Cart Items */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom>
                Cart Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: layoutLightGreenColor,
                    }}
                  >
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Product Name
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="center"
                    >
                      Qty
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Item Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.cart?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">₹{item.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isNaN(n) ? fallback : Math.round(n * 100) / 100;
};

const formatDate = (raw) => {
  const d = new Date(raw ?? Date.now());
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  return {
    date: d.toLocaleDateString("en-CA"), // YYYY-MM-DD
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

// central mapping helper so you don't duplicate mapping code
const mapApiOrdersToOrders = (apiOrders = [], paginationModelRef) => {
  return apiOrders.map((o, index) => {
    const { date: orderDateStr, time: orderTimeStr } = formatDate(o.order_date);

    const cart = (o.BillAppOrderProducts ?? []).map((item) => {
      const p = item.BillAppProduct ?? {};
      const quantity = safeNumber(item.quantity, 1);
      const price = safeNumber(item.price, 0);
      return {
        product_name: p.product_name ?? p.name ?? "Unknown",
        quantity,
        price,
        batch_number: item.batch_number ?? p.batch_number ?? "",
        unit: item.unit ?? p.unit ?? "",
        discount: safeNumber(item.discount ?? 0, 0),
        gst: item.gst ?? o.gst ?? 0,
      };
    });

    const gst = safeNumber(o.gst ?? 0, 0);
    const total = o.total_amount ?? null;

    const paymentMethod = o.payment_method ?? o.paymentMethod ?? "Cash";

    const page = paginationModelRef?.page ?? 0;
    const pageSize = paginationModelRef?.pageSize ?? 10;

    return {
      sno: page * pageSize + index + 1,
      invoice_number: o.order_id,
      order_id: o.order_id,
      order_date: orderDateStr,
      order_time: orderTimeStr,
      gst,
      total,
      paymentMethod,
      cart,
    };
  });
};

// return "YYYY-MM-DD HH:MM:SS" strings for API when user selected only dates
const toApiDateTime = (dateStr, endOfDay = false) => {
  if (!dateStr) return "";
  // if already contains time, pass-through
  if (dateStr.includes(" ") && dateStr.length >= 19) return dateStr;
  // dateStr is YYYY-MM-DD (from input[type=date])
  return endOfDay ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
};

const COLUMNS_COUNT = 7;

function DNRBillTable() {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [lastDate, setLastDate] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0); // totalUsers from backend
  const [activeFilter, setActiveFilter] = useState(null); // null means no date filter

  useEffect(() => {
    fetchStoreOrders(activeFilter);
  }, [paginationModel]);

  const fetchStoreOrders = async (filters = null) => {
    try {
      setLoading(true);
      const userData = LsService.getItem(storageKey);
      if (!userData?.user_id) {
        console.warn("No user_id in local storage");
        setLoading(false);
        return;
      }

      const reqParams = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      };

      const reqBody = {
        user_id: userData.user_id,
        ...(filters
          ? { startDate: filters.startDate, endDate: filters.endDate }
          : {}),
      };

      const response = await getAllDNROrders(reqParams, reqBody);

      const apiData = response.data || {};
      const apiOrders = apiData.data || [];
      const totalUsers = apiData.totalOrders || 0;

      const mappedOrders = mapApiOrdersToOrders(apiOrders, paginationModel);

      setOrders(mappedOrders);
      setRowCount(totalUsers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onGetDataByDates = async () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    const apiStart = toApiDateTime(startDate, false);
    const apiEnd = toApiDateTime(lastDate, true);
    const filters = { startDate: apiStart, endDate: apiEnd };
    setActiveFilter(filters);
    await fetchStoreOrders(filters);
  };

  const onDownloadxl = () => {
    if (!orders || orders.length === 0) {
      alert("No billing data available to download.");
      return;
    }

    // rows to export
    const exportData = [];

    orders.forEach((order, index) => {
      const invoiceOnce = {
        sno: order.sno ?? index + 1,
        date_inv: `${order.order_date} ${order.order_time}`,
        inv_no: `INV${order.invoice_number}`,
        payment_method: order.paymentMethod,
        gst: `${order.gst}₹`,
        inv_value: `${order.total}₹`,
      };

      order.cart.forEach((item, idx) => {
        exportData.push({
          S_No: idx === 0 ? invoiceOnce.sno : "",
          Date_of_Inv: idx === 0 ? invoiceOnce.date_inv : "",
          Inv_No: idx === 0 ? invoiceOnce.inv_no : "",
          Payment_Type: idx === 0 ? invoiceOnce.payment_method : "",
          GST_Inclusive: idx === 0 ? invoiceOnce.gst : "",
          Inv_Value: idx === 0 ? invoiceOnce.inv_value : "",

          Material_Name: item.product_name || "",
          Quantity: item.quantity || 1,
          Items_Total_Amt: `${item.price}₹` || 0,
        });
      });
    });

    // file name
    const filename = `Billings_${Date.now()}.xlsx`;

    // create workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 5 }, // S_No
      { wch: 22 }, // Date_of_Inv
      { wch: 10 }, // Inv_No
      { wch: 10 }, // Inv_No
      { wch: 15 }, // GST (Inclusive)
      { wch: 10 }, // Inv_Value
      { wch: 30 }, // Material_Name
      { wch: 10 }, // Item_Price
      { wch: 10 }, // Quantity
      { wch: 15 }, // Item_Total_Amt
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billings");
    XLSX.writeFile(workbook, filename);
  };

  return (
    <Box>
      {/* Large screen view */}
      <Box sx={lrgScreenStyle}>
        <Box sx={headerBoxStyle}>
          <DNRMainNavHeader
            headerTitle="Bills Management"
            headerNavStartTitle="Home / Admin Dashboard"
            homeNavigate="/dnr_super_admin"
            headerNavEndTitle="Billings"
          />
        </Box>

        <Box sx={rightInnerBoxStyle}>
          <Box
            sx={{
              width: "96%",
              p: 2,
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Box sx={{ p: 2 }}>
                <Grid
                  item
                  size={12}
                  container
                  spacing={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    p: 2,
                    borderRadius: "10px",
                    mb: 1,
                    boxShadow: 2,
                  }}
                >
                  <Grid item size={3}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="start_date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)} // <-- fixed
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item size={3}>
                    <TextField
                      fullWidth
                      label="Last Date"
                      name="last_date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={lastDate}
                      onChange={(e) => setLastDate(e.target.value)} // <-- fixed
                      disabled={!startDate || loading}
                      inputProps={{
                        min: startDate || undefined,
                      }}
                    />
                  </Grid>
                  <Grid item size={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={() => onGetDataByDates()}
                      disabled={!startDate || !lastDate || loading}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                      }}
                    >
                      Get Data
                    </Button>
                  </Grid>
                  <Grid item size={1}></Grid>
                  <Grid item size={3}>
                    <Button
                      fullWidth
                      color="primary"
                      variant="outlined"
                      onClick={() => onDownloadxl()}
                      disabled={loading || orders.length === 0}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                      }}
                    >
                      Download Current list <DownloadIcon sx={{ ml: 1 }} />
                    </Button>
                  </Grid>
                </Grid>

                <TableContainer component={Paper}>
                  <Table>
                    {loading ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={COLUMNS_COUNT} align="center">
                            <CircularProgress color="primary" />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : orders.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={COLUMNS_COUNT} align="center">
                            No data available
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <>
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: layoutDarkGreenColor,
                            }}
                          >
                            <TableCell />
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              S No
                            </TableCell>
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              Invoice No
                            </TableCell>
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              Order Date
                            </TableCell>
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              Payment
                            </TableCell>
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              GST
                            </TableCell>
                            <TableCell
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orders.map((order) => (
                            <Row key={order.order_id} order={order} />
                          ))}
                        </TableBody>
                      </>
                    )}
                  </Table>
                  {orders.length > 0 && (
                    <TablePagination
                      component="div"
                      count={rowCount} // totalUsers from server
                      page={paginationModel.page}
                      onPageChange={(_e, newPage) =>
                        setPaginationModel((prev) => ({
                          ...prev,
                          page: newPage,
                        }))
                      }
                      rowsPerPage={paginationModel.pageSize}
                      onRowsPerPageChange={(event) => {
                        const newSize = parseInt(event.target.value, 10);
                        setPaginationModel({ page: 0, pageSize: newSize });
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  )}
                </TableContainer>
                {rowCount < 10 ? <Box p={2} /> : <Box p={8} />}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile View warning*/}
      <SmallScreenError />
    </Box>
  );
}

export default DNRBillTable;
